import { Fragment, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ChevronDown, Check } from "lucide-react";
import { I18nProvider, useT, LOCALES } from "@/i18n";
import About from "@/About";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSETS = {
  logo:
    "https://customer-assets.emergentagent.com/job_a438a532-af71-4940-9c20-236f23594fbe/artifacts/duyyat7i_Logo_de_Hacienda_Esmeralda__1___1_-removebg-preview.png",
  scene:
    "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/cjb8ntqy_La%20Esmeralda-38.jpg",
  tokenWatermark:
    "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/onlrht3n_La%20Esmeralda-18.jpg",
};

const OPPORTUNITY_ITEMS = [
  { n: "01", tKey: "opportunity.1" },
  { n: "02", tKey: "opportunity.2" },
  { n: "03", tKey: "opportunity.3" },
  { n: "04", tKey: "opportunity.4" },
  { n: "05", tKey: "opportunity.5" },
  { n: "06", tKey: "opportunity.6" },
];

const WHY_ITEMS = [
  { n: "I.", tKey: "why.1" },
  { n: "II.", tKey: "why.2" },
  { n: "III.", tKey: "why.3" },
];

const QA_ITEMS = [
  { tKey: "qa.1" },
  { tKey: "qa.2" },
  { tKey: "qa.3" },
  { tKey: "qa.4" },
  { tKey: "qa.5" },
  { tKey: "qa.6" },
];

const NAV_SECTIONS = [
  { id: "opportunity", key: "nav.opportunity", testid: "nav-opportunity" },
  { id: "why", key: "nav.why", testid: "nav-why" },
];

const LanguageSwitcher = () => {
  const { lang, setLang, t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = LOCALES.find((l) => l.code === lang) || LOCALES[0];

  return (
    <div className="relative" ref={ref} data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="lang-switcher-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.label")}
        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)] hover:text-[var(--ink)] transition-colors"
      >
        <span>{current.short}</span>
        <ChevronDown
          size={12}
          strokeWidth={1.8}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <ul
          role="listbox"
          data-testid="lang-switcher-menu"
          className="absolute right-0 mt-3 min-w-[150px] bg-[var(--cream)] border border-[rgba(31,61,43,0.12)] rounded-sm shadow-[0_12px_30px_rgba(18,29,22,0.08)] py-1 z-50"
        >
          {LOCALES.map((loc) => {
            const active = loc.code === lang;
            return (
              <li key={loc.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  data-testid={`lang-option-${loc.code}`}
                  onClick={() => {
                    setLang(loc.code);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-3 px-4 py-2 text-left text-[13px] transition-colors ${
                    active
                      ? "text-[var(--ink)]"
                      : "text-[var(--muted-ink)] hover:text-[var(--ink)] hover:bg-[rgba(31,61,43,0.04)]"
                  }`}
                >
                  <span>{t(loc.labelKey)}</span>
                  {active ? (
                    <Check size={13} strokeWidth={1.8} className="text-[var(--green)]" />
                  ) : (
                    <span className="text-[10px] tracking-[0.2em] uppercase opacity-60">
                      {loc.short}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const Header = () => {
  const { t } = useT();
  const [active, setActive] = useState("");
  const location = useLocation();
  const isAbout = location.pathname === "/about";

  useEffect(() => {
    const ids = [...NAV_SECTIONS.map((s) => s.id), "contact"];
    const targets = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);
    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible && visible.target && visible.target.id) {
          setActive(visible.target.id);
        }
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    targets.forEach((t) => observer.observe(t));
    return () => observer.disconnect();
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm bg-[rgba(251,249,244,0.82)] border-b border-[rgba(31,61,43,0.08)]"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 py-4">
        <a
          href="#top"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={ASSETS.logo}
            alt="Esmeralda Partners"
            className="h-9 w-9 object-contain"
            data-testid="brand-logo"
          />
          <div className="font-serif text-[17px] tracking-[0.01em] text-[var(--ink)] leading-tight">
            Esmeralda Partners
          </div>
        </a>
        <nav className="hidden md:flex items-center gap-8 text-[13px] text-[var(--muted-ink)]">
          {NAV_SECTIONS.map((s) => {
            const isActive = active === s.id;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                data-testid={s.testid}
                data-active={isActive ? "true" : "false"}
                className={`relative transition-colors pb-1 ${
                  isActive ? "text-[var(--ink)]" : "hover:text-[var(--ink)]"
                }`}
              >
                {t(s.key)}
                <span
                  aria-hidden
                  className={`absolute left-0 right-0 -bottom-0.5 h-px bg-[var(--green)] transition-opacity duration-300 ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                />
              </a>
            );
          })}
          <Link
            to="/about"
            data-testid="nav-about"
            data-active={isAbout ? "true" : "false"}
            className={`relative transition-colors pb-1 ${
              isAbout ? "text-[var(--ink)]" : "hover:text-[var(--ink)]"
            }`}
          >
            {t("footer.about_link")}
            <span
              aria-hidden
              className={`absolute left-0 right-0 -bottom-0.5 h-px bg-[var(--green)] transition-opacity duration-300 ${
                isAbout ? "opacity-100" : "opacity-0"
              }`}
            />
          </Link>
          <a
            href="#contact"
            data-testid="nav-contact-cta"
            data-active={active === "contact" ? "true" : "false"}
            className={`inline-flex items-center gap-2 px-4 py-2 border rounded-full text-[12px] tracking-[0.18em] uppercase transition-colors ${
              active === "contact"
                ? "bg-[var(--green)] text-white border-[var(--green)]"
                : "border-[var(--green)] text-[var(--green)] hover:bg-[var(--green)] hover:text-white"
            }`}
          >
            {t("nav.start_conversation")}
            <span aria-hidden>→</span>
          </a>
          <LanguageSwitcher />
        </nav>
        <div className="md:hidden flex items-center gap-4">
          <LanguageSwitcher />
          <a
            href="#contact"
            data-testid="mobile-cta"
            className="text-[11px] tracking-[0.2em] uppercase text-[var(--green)]"
          >
            {t("nav.contact_short")}
          </a>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  const { t } = useT();
  const imgRef = useRef(null);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        const y = window.scrollY || window.pageYOffset;
        // subtle parallax: image drifts down as page scrolls, clamped at 40px
        const offset = Math.min(y * 0.18, 40);
        if (imgRef.current) {
          imgRef.current.style.transform = `translate3d(0, ${offset}px, 0)`;
        }
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[720px] w-full overflow-hidden bg-[var(--cream)]"
    >
      {/* Single-image parallax background */}
      <div
        ref={imgRef}
        aria-hidden
        data-testid="hero-parallax-image"
        className="parallax-layer will-change-transform"
        style={{
          backgroundImage: `url(${ASSETS.scene})`,
          backgroundPosition: "center bottom",
          backgroundSize: "cover",
          top: "-40px",
          bottom: "-40px",
        }}
      />

      {/* Subtle dark gradient overlay on image for depth and legibility */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(18,29,22,0) 55%, rgba(18,29,22,0.18) 80%, rgba(18,29,22,0.32) 100%)",
        }}
      />

      {/* Soft wash behind headline for legibility */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[48%] pointer-events-none z-[1]"
        style={{
          background:
            "linear-gradient(to bottom, rgba(250,247,239,0.96) 0%, rgba(250,247,239,0.88) 40%, rgba(250,247,239,0.6) 75%, rgba(250,247,239,0) 100%)",
        }}
      />

      {/* Copy */}
      <div className="relative z-10 h-full flex flex-col items-center justify-start text-center px-6 pt-28 md:pt-32 lg:pt-36">
        <div
          data-testid="hero-eyebrow"
          className="text-[10px] md:text-[11px] tracking-[0.34em] uppercase text-[var(--green)] mb-6 md:mb-8"
        >
          {t("hero.eyebrow")}
        </div>
        <h1
          data-testid="hero-headline"
          className="font-serif text-[var(--ink)] leading-[1.05] tracking-[-0.01em] text-4xl sm:text-5xl lg:text-6xl max-w-[18ch] mx-auto"
        >
          {t("hero.headline")}
        </h1>
        <p
          data-testid="hero-subheadline"
          className="mt-6 md:mt-7 font-serif italic text-[var(--muted-ink)] text-lg sm:text-xl lg:text-2xl max-w-[32ch] mx-auto"
        >
          {t("hero.subheadline")}
        </p>

        <a
          href="#contact"
          data-testid="hero-cta"
          className="mt-8 md:mt-10 inline-flex items-center gap-3 text-[11px] tracking-[0.28em] uppercase text-[var(--ink)] border-b border-[var(--ink)] pb-1 hover:text-[var(--green)] hover:border-[var(--green)] transition-colors"
        >
          {t("hero.cta")}
          <span aria-hidden>↓</span>
        </a>
      </div>

      {/* Scroll hint */}
      <div
        aria-hidden
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[10px] tracking-[0.3em] uppercase text-[var(--muted-ink)] flex flex-col items-center gap-2"
      >
        <span>{t("hero.scroll")}</span>
        <span className="block w-px h-8 bg-[var(--muted-ink)] opacity-60" />
      </div>
    </section>
  );
};

const METRICS = [
  { k: "30+", vKey: "metric.years" },
  { k: "1,400", vKey: "metric.hectares" },
  { k: "metric.active_prefix", vKey: "metric.operations", kIsKey: true },
  { k: "metric.on_the_ground_prefix", vKey: "metric.execution", kIsKey: true },
];

const YOUTUBE_ID = "FeViJh9F4ZE";

const VideoSection = () => {
  const { t } = useT();
  const [open, setOpen] = useState(false);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    // lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <section
      data-testid="intro-section"
      className="relative bg-[var(--cream)] text-[var(--ink)]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 pt-24 md:pt-36 pb-20 md:pb-24">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-10 md:mb-14">
          {t("video.eyebrow")}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          {/* Left — statement */}
          <div className="md:col-span-5">
            <h2
              data-testid="positioning-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em] text-[var(--ink)]"
            >
              {t("video.headline_main")}
              <br />
              <span className="font-sans font-bold not-italic text-[var(--green)] text-[0.88em] tracking-[-0.01em]">
                {t("video.headline_accent")}
              </span>
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[var(--muted-ink)] max-w-[44ch]">
              {t("video.body")}
            </p>
          </div>

          {/* Right — video card */}
          <div className="md:col-span-7">
            <button
              type="button"
              onClick={() => setOpen(true)}
              data-testid="video-open-button"
              aria-label={t("video.watch")}
              className="group relative block w-full overflow-hidden rounded-sm bg-[var(--ink)] aspect-[16/10] md:aspect-[16/9] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--green)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--cream)]"
            >
              <img
                src={ASSETS.scene}
                alt="Esmeralda Partners — livestock at dawn"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.02]"
                loading="lazy"
              />
              {/* dim overlay */}
              <div
                aria-hidden
                className="absolute inset-0 bg-[rgba(18,29,22,0.22)] group-hover:bg-[rgba(18,29,22,0.32)] transition-colors duration-300"
              />
              {/* play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full bg-[rgba(250,247,239,0.92)] text-[var(--green)] shadow-[0_10px_40px_rgba(18,29,22,0.25)] transition-transform duration-300 group-hover:scale-[1.06]">
                  <svg
                    width="28"
                    height="28"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden
                    className="ml-[3px]"
                  >
                    <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.29-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14z" />
                  </svg>
                </span>
              </div>
              <div className="absolute left-4 bottom-4 md:left-6 md:bottom-6 text-[10px] tracking-[0.3em] uppercase text-[rgba(250,247,239,0.85)]">
                {t("video.watch")}
              </div>
            </button>
          </div>
        </div>

        {/* Metrics strip */}
        <div
          data-testid="metrics-row"
          className="mt-20 md:mt-28 border-t border-[rgba(31,61,43,0.12)] pt-10 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-10"
        >
          {METRICS.map((m, i) => {
            const labelText = t(m.vKey);
            const kText = m.kIsKey ? t(m.k) : m.k;
            return (
              <div
                key={i}
                data-testid={`metric-${m.vKey}`}
              >
                <div className="font-serif text-3xl md:text-[40px] text-[var(--ink)] leading-none tracking-[-0.01em]">
                  {kText}
                </div>
                <div className="mt-3 text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)]">
                  {labelText}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {open && (
        <div
          data-testid="video-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Esmeralda Partners video"
          className="fixed inset-0 z-[60] flex items-center justify-center px-4 md:px-8 bg-[rgba(10,15,12,0.86)] backdrop-blur-sm"
          onClick={() => setOpen(false)}
        >
          <div
            className="relative w-full max-w-[1100px] aspect-video bg-black shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              data-testid="video-close"
              aria-label={t("video.close")}
              onClick={() => setOpen(false)}
              className="absolute -top-12 right-0 text-[11px] tracking-[0.3em] uppercase text-[var(--cream)]/85 hover:text-white inline-flex items-center gap-3"
            >
              {t("video.close")}
              <span
                aria-hidden
                className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(250,247,239,0.35)] hover:border-white transition-colors"
              >
                ×
              </span>
            </button>
            <iframe
              data-testid="video-iframe"
              src={`https://www.youtube.com/embed/${YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title="Esmeralda Partners — Hacienda Esmeralda"
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

const OpportunityGrid = () => {
  const { t } = useT();
  return (
    <section
      id="opportunity"
      data-testid="opportunity-section"
      className="relative bg-[var(--bone)] text-[var(--ink)] border-t border-[rgba(31,61,43,0.08)]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 pt-20 md:pt-24 pb-24 md:pb-36">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16 md:mb-24">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-5">
              {t("opportunity.eyebrow")}
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em] max-w-[20ch]">
              {t("opportunity.title")}
            </h2>
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)]">
            {t("opportunity.tagline")}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[rgba(31,61,43,0.1)]">
          {OPPORTUNITY_ITEMS.map((item) => (
            <div
              key={item.n}
              data-testid={`opportunity-${item.n}`}
              className="opportunity-cell group relative p-8 md:p-10 lg:p-12 border-r border-b border-[rgba(31,61,43,0.1)] transition-colors duration-300 hover:bg-[var(--cream)]"
            >
              <div className="flex items-start justify-between mb-10">
                <span className="font-serif text-[13px] text-[var(--green)] tracking-wider">
                  {item.n}
                </span>
                <span className="text-[var(--muted-ink)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs tracking-[0.2em] uppercase">
                  →
                </span>
              </div>
              <h3 className="font-serif text-xl md:text-2xl leading-snug text-[var(--ink)] group-hover:text-[var(--green)] transition-colors">
                {t(`${item.tKey}.title`)}
              </h3>
              <p className="mt-4 text-[14px] leading-relaxed text-[var(--muted-ink)] max-w-[40ch]">
                {t(`${item.tKey}.desc`)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TOKEN_FLOW_STEPS = [
  {
    tKey: "flow.1",
    img: "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/0rr32jx9_Capital.png",
  },
  {
    tKey: "flow.2",
    img: "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/kskc6412_Tokenization.png",
  },
  {
    tKey: "flow.3",
    img: "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/b2ktum1g_Asset%20Acquisition.png",
  },
  {
    tKey: "flow.4",
    img: "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/na1b2vi6_Bilogical%20Growth..png",
  },
  {
    tKey: "flow.5",
    img: "https://customer-assets.emergentagent.com/job_farm-capital/artifacts/5if95e8c_Sales%20%26%20Payback.png",
  },
];

const TokenizationExplainSection = () => {
  const { t } = useT();
  return (
    <section
      id="tokenization"
      data-testid="tokenization-section"
      className="relative bg-[var(--cream)] text-[var(--ink)] border-t border-[rgba(31,61,43,0.08)]"
    >
      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
        {/* Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 mb-16 md:mb-20">
          <div className="md:col-span-6">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-6">
              {t("tokenization.eyebrow")}
            </div>
            <h2
              data-testid="tokenization-title"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em]"
            >
              {t("tokenization.title")}
            </h2>
          </div>
          <div className="md:col-span-6 md:pt-2">
            <p
              data-testid="tokenization-subtitle"
              className="text-[15px] md:text-base leading-relaxed text-[var(--muted-ink)] max-w-[50ch]"
            >
              {t("tokenization.subtitle")}
            </p>
          </div>
        </div>

        {/* Divider label */}
        <div
          data-testid="tokenization-divider"
          className="flex items-center gap-5 md:gap-8 mb-14 md:mb-20"
        >
          <span className="flex-1 h-px bg-[rgba(31,61,43,0.18)]" />
          <span className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[var(--green)] text-center">
            {t("tokenization.divider")}
          </span>
          <span className="flex-1 h-px bg-[rgba(31,61,43,0.18)]" />
        </div>

        {/* Five-step flow — single responsive row.  On desktop: [STEP]—[LINE]—[STEP]…; on mobile: wraps to stacked steps without connectors. */}
        <ol
          data-testid="tokenization-flow"
          className="flex flex-wrap md:flex-nowrap items-start gap-y-12 md:gap-y-0"
        >
          {TOKEN_FLOW_STEPS.map((step, i) => {
            const isLast = i === TOKEN_FLOW_STEPS.length - 1;
            return (
              <Fragment key={step.tKey}>
                <li
                  data-testid={`flow-step-${i + 1}`}
                  data-reveal-delay={i * 120}
                  className="flow-card reveal flex-shrink-0 w-full sm:w-[calc(50%-12px)] md:w-[180px] lg:w-[200px] flex flex-col items-center text-center"
                >
                  <div className="flow-circle relative w-[120px] h-[120px] rounded-full border-[1.5px] border-[var(--gold)] bg-[var(--cream)] flex items-center justify-center overflow-hidden">
                    <img
                      src={step.img}
                      alt=""
                      aria-hidden="true"
                      className="flow-img w-[84px] h-[84px] object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div className="mt-6 text-[10px] tracking-[0.34em] uppercase text-[var(--gold)]">
                    {t("tokenization.step_label")} 0{i + 1}
                  </div>
                  <div className="mt-3 font-serif text-xl text-[var(--ink)] leading-tight tracking-[-0.005em]">
                    {t(`${step.tKey}.title`)}
                  </div>
                  <p className="mt-3 text-[13px] leading-relaxed text-[var(--muted-ink)] max-w-[22ch]">
                    {t(`${step.tKey}.desc`)}
                  </p>
                </li>
                {!isLast && (
                  <div
                    aria-hidden
                    className="hidden md:flex flex-1 items-center gap-2 self-start"
                    style={{ marginTop: "60px" }}
                  >
                    <span className="w-[6px] h-[6px] rounded-full bg-[var(--gold)]" />
                    <span className="flex-1 h-px bg-[rgba(31,61,43,0.18)]" />
                    <span className="w-[6px] h-[6px] rounded-full bg-[var(--gold)]" />
                  </div>
                )}
              </Fragment>
            );
          })}
        </ol>
      </div>
    </section>
  );
};

const TOKEN_STATS = {
  name: "Cattle Token",
  status: "Coming Soon",
  totalRaised: 0,
  goal: 5_000_000,
  currency: "USD",
  countdown: { days: 42, hours: 12, minutes: 30, seconds: 0 },
};

const TOKEN_TABS = [
  { key: "token.tab.overview", id: "overview" },
  { key: "token.tab.tokenomics", id: "tokenomics" },
  { key: "token.tab.allocation", id: "allocation" },
];

const TokenSection = () => {
  const { t, lang } = useT();
  const pct =
    TOKEN_STATS.goal > 0
      ? Math.min(100, Math.round((TOKEN_STATS.totalRaised / TOKEN_STATS.goal) * 100))
      : 0;
  const fmt = (n) =>
    new Intl.NumberFormat(
      lang === "es" ? "es-VE" : lang === "de" ? "de-DE" : "en-US",
      {
        style: "currency",
        currency: TOKEN_STATS.currency,
        maximumFractionDigits: 0,
      }
    ).format(n);

  return (
    <section
      id="token"
      data-testid="token-section"
      className="relative bg-[var(--cream)] text-[var(--ink)] overflow-hidden"
    >
      {/* Watermark background image */}
      <div
        aria-hidden
        data-testid="token-watermark"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: `url(${ASSETS.tokenWatermark})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.08,
        }}
      />
      {/* Soft cream overlay so cards stay readable */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(250,247,239,0.92) 0%, rgba(250,247,239,0.78) 50%, rgba(250,247,239,0.92) 100%)",
        }}
      />

      <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 py-20 md:py-28">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)]">
            {t("token.eyebrow")}
          </div>
          <div className="text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)]">
            {t("token.brickken")}
          </div>
        </div>

        {/* Token card */}
        <div
          data-testid="token-card"
          className="border border-[rgba(31,61,43,0.14)] bg-white/75 backdrop-blur-[2px] rounded-sm overflow-hidden shadow-[0_1px_0_rgba(31,61,43,0.04)]"
        >
          {/* Card header — tabs (visual only) */}
          <div
            data-testid="token-tabs"
            role="tablist"
            aria-label="Token details"
            className="flex items-end gap-1 md:gap-2 px-5 md:px-8 pt-5 border-b border-[rgba(31,61,43,0.1)]"
          >
            {TOKEN_TABS.map((tab, i) => {
              const active = i === 0;
              return (
                <div
                  key={tab.id}
                  role="tab"
                  aria-selected={active}
                  tabIndex={-1}
                  data-testid={`token-tab-${tab.id}`}
                  data-active={active ? "true" : "false"}
                  className={`relative px-4 md:px-6 py-3 text-[12px] tracking-[0.22em] uppercase select-none ${
                    active
                      ? "text-[var(--ink)]"
                      : "text-[var(--muted-ink)] opacity-60"
                  }`}
                >
                  {t(tab.key)}
                  {active && (
                    <span
                      aria-hidden
                      className="absolute left-3 right-3 -bottom-px h-[2px] bg-[var(--green)]"
                    />
                  )}
                </div>
              );
            })}
            <div className="ml-auto py-3">
              <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[var(--green)] border border-[var(--green)] px-3 py-1 rounded-full">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--green)]" />
                {t("token.coming_soon")}
              </span>
            </div>
          </div>

          {/* Card body */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 p-6 md:p-10">
            {/* Left — identity */}
            <div className="md:col-span-5 flex flex-col justify-between gap-10">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-3">
                  {t("token.label")}
                </div>
                <div
                  data-testid="token-name"
                  className="font-serif text-2xl md:text-3xl text-[var(--ink)] leading-tight"
                >
                  {TOKEN_STATS.name}
                  <span className="block mt-1 font-sans not-italic text-[11px] tracking-[0.22em] uppercase text-[var(--green)]">
                    {t("token.coming_soon")}
                  </span>
                </div>
                <p className="mt-5 text-[13px] leading-relaxed text-[var(--muted-ink)] max-w-[42ch]">
                  {t("token.description")}
                </p>
              </div>

              <dl className="grid grid-cols-2 gap-x-6 gap-y-6">
                <div>
                  <dt className="text-[10px] tracking-[0.26em] uppercase text-[var(--muted-ink)]">
                    {t("token.asset_class")}
                  </dt>
                  <dd className="mt-2 font-serif text-lg text-[var(--ink)]">
                    {t("token.livestock")}
                  </dd>
                </div>
                <div>
                  <dt className="text-[10px] tracking-[0.26em] uppercase text-[var(--muted-ink)]">
                    {t("token.jurisdiction")}
                  </dt>
                  <dd className="mt-2 font-serif text-lg text-[var(--ink)]">
                    {t("token.venezuela")}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Right — raise + countdown */}
            <div className="md:col-span-7 md:border-l md:border-[rgba(31,61,43,0.1)] md:pl-10">
              <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-2">
                    {t("token.total_raised")}
                  </div>
                  <div
                    data-testid="token-total-raised"
                    className="font-serif text-[40px] md:text-[56px] leading-none text-[var(--ink)] tracking-[-0.01em]"
                  >
                    {fmt(TOKEN_STATS.totalRaised)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-2">
                    {t("token.goal")}
                  </div>
                  <div
                    data-testid="token-goal"
                    className="font-serif text-xl md:text-2xl text-[var(--muted-ink)]"
                  >
                    {fmt(TOKEN_STATS.goal)}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div
                data-testid="token-progress"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                className="relative h-[6px] bg-[rgba(31,61,43,0.1)] rounded-full overflow-hidden"
              >
                <span
                  className="absolute inset-y-0 left-0 bg-[var(--green)] rounded-full"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] tracking-[0.2em] uppercase text-[var(--muted-ink)]">
                <span data-testid="token-progress-label">
                  {pct}% {t("token.raised_suffix")}
                </span>
                <span>{t("token.min_ticket")}</span>
              </div>

              {/* Countdown */}
              <div className="mt-10">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--muted-ink)] mb-4">
                  {t("token.sale_opens")}
                </div>
                <div
                  data-testid="token-countdown"
                  className="grid grid-cols-4 gap-3 md:gap-4 max-w-[440px]"
                >
                  {[
                    { k: "days", v: TOKEN_STATS.countdown.days, tKey: "token.cd.days" },
                    { k: "hours", v: TOKEN_STATS.countdown.hours, tKey: "token.cd.hours" },
                    { k: "min", v: TOKEN_STATS.countdown.minutes, tKey: "token.cd.min" },
                    { k: "sec", v: TOKEN_STATS.countdown.seconds, tKey: "token.cd.sec" },
                  ].map((u) => (
                    <div
                      key={u.k}
                      data-testid={`countdown-${u.k}`}
                      className="border border-[rgba(31,61,43,0.12)] bg-[var(--cream)]/70 rounded-sm py-4 text-center"
                    >
                      <div className="font-serif text-2xl md:text-3xl text-[var(--ink)] tabular-nums leading-none">
                        {String(u.v).padStart(2, "0")}
                      </div>
                      <div className="mt-2 text-[9px] tracking-[0.28em] uppercase text-[var(--muted-ink)]">
                        {t(u.tKey)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-8 flex items-center gap-4 flex-wrap">
                <button
                  type="button"
                  data-testid="token-register-interest"
                  onClick={() =>
                    document
                      .getElementById("contact")
                      ?.scrollIntoView({ behavior: "smooth" })
                  }
                  className="inline-flex items-center gap-3 px-5 py-3 bg-[var(--green)] text-[var(--cream)] text-[11px] tracking-[0.28em] uppercase rounded-full hover:bg-[var(--green-deep)] transition-colors"
                >
                  {t("token.register")}
                  <span aria-hidden>→</span>
                </button>
                <span className="text-[11px] tracking-[0.22em] uppercase text-[var(--muted-ink)]">
                  {t("token.whitelist")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const WhySection = () => {
  const { t } = useT();

  return (
    <section
      id="why"
      data-testid="why-section"
      className="relative bg-[var(--cream)] text-[var(--ink)] overflow-hidden"
    >
      {/* Watermark background — quiet editorial atmosphere, never dominant */}
      <div
        aria-hidden
        className="why-watermark absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://customer-assets.emergentagent.com/job_farm-capital/artifacts/eldqte87_La%20Esmeralda-18.jpg')",
        }}
      />
      {/* Cream gradient overlay — keeps text perfectly readable */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(250,247,239,0.78) 0%, rgba(250,247,239,0.55) 45%, rgba(250,247,239,0.78) 100%)",
        }}
      />
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-6">
            {t("why.eyebrow")}
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em]">
            {t("why.title")}
          </h2>
          <p className="mt-6 text-[14px] md:text-[15px] leading-relaxed text-[var(--muted-ink)] max-w-[38ch]">
            {t("why.body")}
          </p>
        </div>

        <div className="md:col-span-7">
          <ul className="flex flex-col gap-5 md:gap-6">
            {WHY_ITEMS.map((w, i) => (
              <li
                key={w.tKey}
                data-testid={`why-${w.tKey.replace(".", "-")}`}
                data-reveal-delay={i * 140}
                className="why-card reveal grid grid-cols-12 gap-6 px-6 md:px-8 py-7 md:py-9 border border-[rgba(31,61,43,0.1)] bg-white/35 group"
              >
                <div className="col-span-2 md:col-span-1 font-serif text-[var(--green)] text-sm tracking-[0.2em] pt-1">
                  {w.n}
                </div>
                <div className="col-span-10 md:col-span-11">
                  <h3 className="font-serif text-xl md:text-2xl leading-snug text-[var(--ink)] group-hover:text-[var(--green)] transition-colors">
                    {t(`${w.tKey}.title`)}
                  </h3>
                  <p className="mt-3 text-[14px] leading-relaxed text-[var(--muted-ink)] max-w-[58ch]">
                    {t(`${w.tKey}.desc`)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const QASection = () => {
  const { t } = useT();
  const [open, setOpen] = useState(0);

  return (
    <section
      id="qa"
      data-testid="qa-section"
      className="relative bg-[var(--bone)] text-[var(--ink)] border-t border-[rgba(31,61,43,0.08)]"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-36">
        <div className="mb-14 md:mb-20">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green)] mb-6">
            {t("qa.eyebrow")}
          </div>
          <h2
            data-testid="qa-title"
            className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-[-0.005em]"
          >
            {t("qa.title")}
          </h2>
        </div>

        <ul className="flex flex-col gap-3 md:gap-4">
          {QA_ITEMS.map((item, i) => {
            const isOpen = open === i;
            return (
              <li
                key={item.tKey}
                data-testid={`qa-item-${i + 1}`}
                data-reveal-delay={i * 90}
                className="qa-item reveal border border-[rgba(31,61,43,0.1)] bg-white/55"
              >
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  data-testid={`qa-trigger-${i + 1}`}
                  aria-expanded={isOpen}
                  aria-controls={`qa-panel-${i + 1}`}
                  className="w-full text-left flex items-center justify-between gap-6 px-5 md:px-7 py-5 md:py-6 group"
                >
                  <span className="flex items-baseline gap-4 md:gap-6">
                    <span className="font-serif text-[var(--green)] text-sm tracking-[0.2em]">
                      0{i + 1}
                    </span>
                    <span className="font-serif text-lg md:text-xl text-[var(--ink)] leading-snug group-hover:text-[var(--green)] transition-colors">
                      {t(`${item.tKey}.q`)}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    className={`flex items-center justify-center w-8 h-8 rounded-full border border-[rgba(31,61,43,0.2)] text-[var(--green)] transition-transform duration-300 ${
                      isOpen ? "rotate-45" : ""
                    }`}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                    >
                      <path d="M6 1v10M1 6h10" />
                    </svg>
                  </span>
                </button>
                <div
                  id={`qa-panel-${i + 1}`}
                  data-testid={`qa-panel-${i + 1}`}
                  data-open={isOpen ? "true" : "false"}
                  className="qa-panel"
                >
                  <div>
                    <div className="px-5 md:px-7 pb-6 md:pb-7 pt-0 text-[14px] md:text-[15px] leading-relaxed text-[var(--muted-ink)] max-w-[64ch]">
                      {t(`${item.tKey}.a`)}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const { t } = useT();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error(t("contact.toast_required"));
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
      });
      setSuccess(true);
      toast.success(t("contact.toast_success"));
      setForm({ name: "", email: "", phone: "" });
      console.log("lead id:", data?.id);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (Array.isArray(err?.response?.data)
          ? err.response.data[0]?.msg
          : null) ||
        t("contact.toast_generic");
      toast.error(typeof detail === "string" ? detail : t("contact.toast_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[var(--ink)] text-[var(--cream)] overflow-hidden"
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-44">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--green-soft)] mb-6">
          {t("contact.eyebrow")}
        </div>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-[-0.01em] max-w-[16ch]">
          {t("contact.title")}
        </h2>
        <p className="mt-6 text-[15px] leading-relaxed text-[rgba(243,240,229,0.68)] max-w-[46ch]">
          {t("contact.body")}
        </p>

        {success ? (
          <div
            data-testid="contact-success"
            className="mt-14 max-w-[640px] border-t border-[rgba(243,240,229,0.25)] pt-10"
          >
            <div className="font-serif text-2xl md:text-3xl leading-snug">
              {t("contact.success_title")}
            </div>
            <p className="mt-4 text-[14px] text-[rgba(243,240,229,0.65)] max-w-[52ch]">
              {t("contact.success_body")}
            </p>
            <button
              type="button"
              onClick={() => setSuccess(false)}
              data-testid="contact-reset"
              className="mt-8 text-[11px] tracking-[0.28em] uppercase border-b border-[rgba(243,240,229,0.5)] pb-1 hover:text-white hover:border-white transition-colors"
            >
              {t("contact.reset")}
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="mt-14 max-w-[640px]"
            noValidate
          >
            <div className="space-y-10">
              <FieldRow
                label={t("contact.name")}
                testid="field-name"
                value={form.name}
                onChange={update("name")}
                placeholder={t("contact.name_ph")}
                autoComplete="name"
              />
              <FieldRow
                label={t("contact.email")}
                testid="field-email"
                value={form.email}
                onChange={update("email")}
                placeholder={t("contact.email_ph")}
                type="email"
                autoComplete="email"
              />
              <FieldRow
                label={t("contact.phone")}
                testid="field-phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder={t("contact.phone_ph")}
                type="tel"
                autoComplete="tel"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-14 inline-flex items-center gap-4 px-8 py-4 bg-[var(--green)] text-[var(--cream)] text-[11px] tracking-[0.3em] uppercase rounded-full hover:bg-[var(--green-deep)] disabled:opacity-60 transition-colors"
            >
              {submitting ? t("contact.sending") : t("contact.send")}
              <span aria-hidden>→</span>
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

const FieldRow = ({ label, testid, ...props }) => (
  <label className="block group" data-testid={`${testid}-wrap`}>
    <span className="block text-[10px] tracking-[0.3em] uppercase text-[rgba(243,240,229,0.55)]">
      {label}
    </span>
    <input
      {...props}
      data-testid={testid}
      className="mt-3 w-full bg-transparent border-0 border-b border-[rgba(243,240,229,0.25)] py-3 text-lg md:text-xl font-serif text-[var(--cream)] placeholder:text-[rgba(243,240,229,0.25)] focus:outline-none focus:border-[var(--green-soft)] transition-colors"
    />
  </label>
);

const Footer = () => {
  const { t } = useT();
  return (
    <footer
      data-testid="site-footer"
      className="bg-[var(--ink)] text-[rgba(243,240,229,0.55)] border-t border-[rgba(243,240,229,0.08)]"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-12 lg:px-16 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 text-[12px]">
        <div className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="" className="h-7 w-7 object-contain opacity-80" />
          <div className="font-serif text-[var(--cream)] text-[15px]">
            Esmeralda Partners
          </div>
        </div>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6">
          <a
            href="/about"
            data-testid="footer-about-link"
            className="text-[10px] tracking-[0.22em] uppercase hover:text-[var(--cream)] transition-colors"
          >
            {t("footer.about_link")}
          </a>
          <a
            href="mailto:esmeraldapartners@gmail.com"
            data-testid="footer-email-link"
            className="text-[10px] tracking-[0.04em] hover:text-[var(--gold)] transition-colors duration-200"
          >
            esmeraldapartners@gmail.com
          </a>
          <div className="tracking-[0.2em] uppercase text-[10px]">
            {t("footer.copyright", { year: new Date().getFullYear() })}
          </div>
        </div>
      </div>
    </footer>
  );
};

const Home = () => {
  useEffect(() => {
    // optional ping for health (keeps integration wired end-to-end)
    axios.get(`${API}/health`).catch(() => {});
  }, []);

  // Site-wide scroll reveal — observes every .reveal element and fades it in
  // with a staggered delay read from its data-reveal-delay attribute.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll(".reveal"));
    if (els.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -12% 0px" }
    );
    els.forEach((el) => {
      const delay = el.getAttribute("data-reveal-delay");
      if (delay) el.style.transitionDelay = `${delay}ms`;
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Gentle parallax for the Why watermark — translates the background a few
  // pixels slower than scroll for a subtle depth cue. rAF-throttled.
  useEffect(() => {
    const el = document.querySelector(".why-watermark");
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = el.parentElement.getBoundingClientRect();
        const progress =
          (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        const y = Math.max(-16, Math.min(16, (progress - 0.5) * 32));
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0) scale(1.04)`;
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main data-testid="landing-page" className="bg-[var(--cream)]">
      <Header />
      <Hero />
      <VideoSection />
      <OpportunityGrid />
      <TokenizationExplainSection />
      <TokenSection />
      <WhySection />
      <QASection />
      <ContactSection />
      <Footer />
      <Toaster position="bottom-right" theme="light" richColors closeButton />
    </main>
  );
};

export default function App() {
  return (
    <I18nProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
          </Routes>
        </BrowserRouter>
      </div>
    </I18nProvider>
  );
}
